import React, { useEffect, useState } from "react";
import {
  Table, Tag, Button, message, Modal, Drawer, Divider,
  InputNumber, Form, Input, Spin, Alert, Space, Tooltip,
  Dropdown, Typography, Card, Statistic, Row, Col,
  Timeline, Badge, Progress
} from "antd";
import {
  WalletOutlined, ReloadOutlined, FileExcelOutlined, FilePdfOutlined,
  DownOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
  SyncOutlined, DollarOutlined, RiseOutlined, CalendarOutlined,
  ExclamationCircleOutlined, ArrowRightOutlined, BankOutlined,
  EyeOutlined, IdcardOutlined, FileTextOutlined, UserOutlined,
  PercentageOutlined, CreditCardOutlined, HistoryOutlined,
} from "@ant-design/icons";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, ResponsiveContainer,
} from "recharts";
import {
  StaffTransactionsGet,
  StaffTransactionsPost,
  StaffWithdrawPost,
  StaffWithdrawalsGet,
  UserGet
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../../Admin_Panel/Utils/exportCSV";
import { exportToPDF } from "../../../Admin_Panel/Utils/exportPDF";
import {
  formatDate, formatDateTime, generateRefNumber, capitalize,
} from "../../../Admin_Panel/Utils/formatters";
import "./StaffWithdrawals.css";
import { useNavigate } from "react-router-dom";

const { Text, Title } = Typography;

// ✅ GLOBAL HELPER - Amount with paise (₹150.50)
const formatAmount = (amount) => {
  return parseFloat(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

function StaffWithdrawals() {
  const navigate = useNavigate();
  const staffId = localStorage.getItem("user_id");
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedWd, setSelectedWd] = useState(null);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState(null);
  const [form] = Form.useForm();

  const [stats, setStats] = useState({
    availableBalance: 0,
    totalCommission: 0,
    totalWithdrawn: 0,
    pendingAmount: 0,
    pendingCount: 0,
    completedCount: 0,
    rejectedCount: 0,
    processingCount: 0,
  });

  const [monthlyData, setMonthlyData] = useState([]);

  // ============================================================
  // ✅ FETCH DATA - FIXED with logged-in staff filter
  // ============================================================
  // StaffWithdrawals.jsx - fetchData function fix karo

  const fetchData = async () => {
    try {
      setLoading(true);

      const [txRes, usersRes, withdrawalRes] = await Promise.all([
        StaffTransactionsGet(),
        UserGet(),
        StaffWithdrawalsGet(),
      ]);

      // ✅ Debug
      console.log("📋 Full withdrawalRes:", withdrawalRes);
      console.log("📋 withdrawals array:", withdrawalRes?.withdrawals);

      // ✅ Transactions filter (existing)
      const allTxRaw = Array.isArray(txRes) ? txRes : [];
      const allTx = allTxRaw.filter((t) => {
        return String(t.staff) === String(staffId) ||
          String(t.staff_id) === String(staffId) ||
          (t.staff && String(t.staff.id) === String(staffId));
      });

      console.log("📊 All transactions:", allTxRaw.length);
      console.log("📊 My transactions:", allTx.length);
      setTransactions(allTx);

      // ✅ FIX - Withdrawals extract karo Object se
      let withdrawalData = [];

      if (Array.isArray(withdrawalRes)) {
        withdrawalData = withdrawalRes;
      } else if (withdrawalRes?.withdrawals && Array.isArray(withdrawalRes.withdrawals)) {
        // ✅ Backend response mein withdrawals key hai
        withdrawalData = withdrawalRes.withdrawals;
      }

      console.log("✅ Withdrawals count:", withdrawalData.length);
      setWithdrawals(withdrawalData);

      // ✅ Bank details check
      const allUsers = Array.isArray(usersRes) ? usersRes : [];
      const staff = allUsers.find((u) => String(u.id) === String(staffId));
      setHasBankDetails(!!(
        staff?.bank_account &&
        staff?.ifsc_code &&
        staff?.bank_account !== 'Account Number'
      ));

      // ✅ Balance - Use backend earnings if available
      let totalCommission, completedWithdrawals, pendingWithdrawals, availableBalance;

      if (withdrawalRes?.earnings) {
        // ✅ Backend se direct earnings use karo
        totalCommission = withdrawalRes.earnings.total_commission || 0;
        completedWithdrawals = withdrawalRes.earnings.total_withdrawn || 0;
        pendingWithdrawals = withdrawalRes.earnings.pending_withdrawal || 0;
        availableBalance = withdrawalRes.earnings.available_balance || 0;
      } else {
        // Fallback - Calculate manually
        totalCommission = allTx
          .filter((t) => t.type === "credit" &&
            (t.status === "success" || t.status === "completed"))
          .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

        completedWithdrawals = allTx
          .filter((t) =>
            (t.type === "withdrawal" || t.type === "debit") &&
            (t.status === "completed" || t.status === "approved" ||
              t.status === "processing" || t.status === "success"))
          .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

        pendingWithdrawals = allTx
          .filter((t) =>
            (t.type === "withdrawal" || t.type === "debit") &&
            t.status === "pending")
          .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

        availableBalance = totalCommission - completedWithdrawals - pendingWithdrawals;
      }

      console.log("💰 Staff Balance Calculation:");
      console.log("   Total Commission: ₹", totalCommission);
      console.log("   Completed Withdrawals: ₹", completedWithdrawals);
      console.log("   Pending Withdrawals: ₹", pendingWithdrawals);
      console.log("   Available Balance: ₹", availableBalance);

      // ✅ Stats - Use backend stats if available
      let statsData;
      if (withdrawalRes?.stats) {
        statsData = {
          availableBalance: Math.max(0, availableBalance),
          totalCommission,
          totalWithdrawn: completedWithdrawals,
          pendingAmount: pendingWithdrawals,
          pendingCount: withdrawalRes.stats.pending_count || 0,
          completedCount: withdrawalRes.stats.completed_count || 0,
          rejectedCount: withdrawalRes.stats.rejected_count || 0,
          processingCount: 0,
        };
      } else {
        const withdrawalTx = allTx.filter(
          (t) => t.type === "withdrawal" || t.type === "debit"
        );
        statsData = {
          availableBalance: Math.max(0, availableBalance),
          totalCommission,
          totalWithdrawn: completedWithdrawals,
          pendingAmount: pendingWithdrawals,
          pendingCount: withdrawalTx.filter((t) => t.status === "pending").length,
          completedCount: withdrawalTx.filter(
            (t) => t.status === "completed" || t.status === "approved"
          ).length,
          rejectedCount: withdrawalTx.filter((t) => t.status === "rejected").length,
          processingCount: withdrawalTx.filter((t) => t.status === "processing").length,
        };
      }
      // ✅ Calculate visit commission separately
      const visitCommission = allTx
        .filter((t) =>
          t.type === "credit" &&
          (t.plan_name === 'Profile Visit Commission' ||
            (t.ref_id && String(t.ref_id).startsWith('VISIT-')))
        )
        .reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);

      const planCommission = totalCommission - visitCommission;

      statsData.visitCommission = visitCommission;
      statsData.planCommission = planCommission;

      console.log("💰 Visit Commission: ₹", visitCommission);
      console.log("💰 Plan Commission: ₹", planCommission);

      setStats(statsData);

      setStats(statsData);

      // ✅ Monthly data
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const mt = {};
      months.forEach((m) => (mt[m] = 0));

      withdrawalData.forEach((w) => {
        const d = new Date(w.created_at || w.created_date || w.date);
        if (!isNaN(d)) {
          const month = d.toLocaleString("en-US", { month: "short" });
          if (mt[month] !== undefined) mt[month] += parseFloat(w.amount) || 0;
        }
      });

      setMonthlyData(months.map((m) => ({ month: m, amount: mt[m] })));

    } catch (error) {
      console.error("❌ Fetch error:", error);
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("🚀 Component mounted");
    fetchData();
  }, []);
  // ============================================================
  // ✅ OPEN WITHDRAWAL MODAL
  // ============================================================
  const handleOpenWithdrawModal = () => {
    if (stats.availableBalance <= 0) {
      return message.warning("No commission balance available for withdrawal");
    }

    if (!hasBankDetails) {
      Modal.warning({
        title: "Bank Details Required",
        content: "Please add your bank details before requesting a withdrawal.",
        okText: "Go to Bank Details",
        onOk: () => navigate("/staff/bank"),
      });
      return;
    }

    if (stats.pendingCount > 0) {
      return message.warning("You already have a pending withdrawal request. Please wait for it to be processed.");
    }

    form.resetFields();
    setWithdrawAmount(null);
    setWithdrawModalOpen(true);
  };

  // ============================================================
  // ✅ HANDLE WITHDRAW SUBMIT
  // ============================================================
  const handleWithdrawSubmit = async (values) => {
    const { amount } = values;

    if (!amount || amount <= 0) {
      return message.error("Please enter a valid amount");
    }

    if (amount > stats.availableBalance) {
      return message.error(`Amount cannot exceed available balance: ₹${formatAmount(stats.availableBalance)}`);
    }

    try {
      setWithdrawing(true);

      const response = await StaffWithdrawPost({
        amount: amount,
        payment_method: "bank",
      });

      if (response.success) {
        message.success(response.message || "Withdrawal request submitted successfully! ✅");
        setWithdrawModalOpen(false);
        fetchData();
      } else {
        message.error(response.error || "Failed to submit withdrawal");
      }

    } catch (error) {
      console.error("❌ Withdrawal error:", error);
      message.error(error.message || "Failed to submit withdrawal request");
    } finally {
      setWithdrawing(false);
    }
  };

  // ============================================================
  // ✅ STATUS CONFIG
  // ============================================================
  const getStatusConfig = (status) => {
    const config = {
      pending: {
        color: "pending",
        icon: <ClockCircleOutlined />,
        label: "Pending",
        progress: 25,
        description: "Waiting for admin approval"
      },
      approved: {
        color: "approved",
        icon: <CheckCircleOutlined />,
        label: "Approved",
        progress: 50,
        description: "Approved, processing payment"
      },
      processing: {
        color: "processing",
        icon: <SyncOutlined spin />,
        label: "Processing",
        progress: 75,
        description: "Payment is being processed"
      },
      completed: {
        color: "completed",
        icon: <CheckCircleOutlined />,
        label: "Completed",
        progress: 100,
        description: "Amount paid successfully"
      },
      rejected: {
        color: "rejected",
        icon: <CloseCircleOutlined />,
        label: "Rejected",
        progress: 0,
        description: "Request was rejected"
      },
    };
    return config[status] || config.pending;
  };

  // ============================================================
  // ✅ EXPORT CONFIG
  // ============================================================
  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "ref", label: "Withdrawal Ref", format: (r) => generateRefNumber("withdrawal", r.id) },
    { key: "amount", label: "Amount (₹)", format: (r) => parseFloat(r.amount || 0).toFixed(2) },
    { key: "type", label: "Type", format: (r) => capitalize(r.type) || "Withdrawal" },
    { key: "status", label: "Status", format: (r) => capitalize(r.status) },
    { key: "description", label: "Description", format: (r) => r.description || r.admin_note || "N/A" },
    { key: "date", label: "Request Date", format: (r) => formatDateTime(r.created_date || r.date) },
  ];

  const csvMenu = {
    items: [
      {
        key: "1", label: `All (${withdrawals.length})`,
        onClick: () => exportToCSV(withdrawals, exportColumns, "staff_withdrawals")
      },
    ],
  };

  const pdfMenu = {
    items: [
      {
        key: "1", label: `All (${withdrawals.length})`,
        onClick: () => exportToPDF(withdrawals, exportColumns, {
          title: "Staff Withdrawal History",
          subtitle: "Complete withdrawal records",
          filename: "staff_withdrawals"
        })
      },
    ],
  };

  // ============================================================
  // ✅ TABLE COLUMNS - Updated with decimal format
  // ============================================================
  const columns = [
    {
      title: "#",
      key: "sno",
      width: 45,
      render: (_, __, i) => <span className="swd-sno">{i + 1}</span>
    },
    {
      title: "Type",
      key: "commission_type",
      width: 130,
      render: (_, r) => {
        const isVisit = r.plan_name === 'Profile Visit Commission' ||
          (r.ref_id && String(r.ref_id).startsWith('VISIT-'));
        return isVisit ? (
          <Tag style={{ background: '#f5f3ff', color: '#8b5cf6', border: '1px solid #ddd6fe' }}>
            👀 Visit Commission
          </Tag>
        ) : (
          <Tag style={{ background: '#ecfdf5', color: '#10b981', border: '1px solid #a7f3d0' }}>
            💰 Plan Commission
          </Tag>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (a) => <span className="swd-amount">₹{formatAmount(a)}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (s) => {
        const cfg = getStatusConfig(s);
        return (
          <Tag className={`swd-status-tag swd-status-${cfg.color}`}>
            {cfg.icon} {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: "Request Date",
      key: "date",
      width: 150,
      render: (_, r) => (
        <div className="swd-date-cell">
          <div className="swd-date">{formatDate(r.created_at || r.created_date || r.date)}</div>
          <div className="swd-time">
            {r.created_at ? new Date(r.created_at).toLocaleTimeString("en-US", {
              hour: "2-digit", minute: "2-digit",
            }) : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      fixed: "right",
      render: (_, r) => (
        <Tooltip title="View Details">
          <Button
            size="small"
            icon={<EyeOutlined />}
            className="swd-action-btn"
            onClick={() => { setSelectedWd(r); setDrawerOpen(true); }}
          />
        </Tooltip>
      ),
    },
  ];

  // ============================================================
  // ✅ RENDER
  // ============================================================
  return (
    <div className="swd-page">

      {/* ===== HEADER ===== */}
      <div className="swd-header">
        <div className="swd-header-left">
          <div className="swd-title-row">
            <div className="swd-title-icon"><WalletOutlined /></div>
            <div className="swd-title-content">
              <h2 className="swd-title">Commission Withdrawal</h2>
              <p className="swd-subtitle">Manage your commission balance & withdrawal requests</p>
            </div>
          </div>
        </div>
        <div className="swd-header-actions">
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} className="swd-icon-btn" />
          </Tooltip>
          <Dropdown menu={csvMenu}>
            <Button icon={<FileExcelOutlined />} className="swd-csv-btn">
              <Space size={4}>CSV<DownOutlined style={{ fontSize: 10 }} /></Space>
            </Button>
          </Dropdown>
          <Dropdown menu={pdfMenu}>
            <Button icon={<FilePdfOutlined />} className="swd-pdf-btn">
              <Space size={4}>PDF<DownOutlined style={{ fontSize: 10 }} /></Space>
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* ===== BALANCE + WITHDRAW BUTTON ===== */}
      <div className="swd-balance-section">
        <div className="swd-balance-card">
          <div className="swd-balance-icon"><PercentageOutlined /></div>
          <div className="swd-balance-info">
            <div className="swd-balance-label">Available Commission Balance</div>
            <div className="swd-balance-value">
              ₹{formatAmount(stats.availableBalance)}
            </div>
            <div className="swd-balance-sub">
              Total Commission: ₹{formatAmount(stats.totalCommission)} |
              Withdrawn: ₹{formatAmount(stats.totalWithdrawn)}
            </div>
            {!hasBankDetails && (
              <div className="swd-bank-warning">
                <ExclamationCircleOutlined /> Bank details required
              </div>
            )}
          </div>
          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            className="swd-withdraw-btn"
            onClick={handleOpenWithdrawModal}
            loading={withdrawing}
            disabled={stats.availableBalance <= 0}
          >
            Request Withdrawal
          </Button>
        </div>

        {/* ===== STATS CARDS ===== */}
        <div className="swd-mini-stats">
          <div className="swd-mini-card">
            <DollarOutlined className="swd-mini-icon withdrawn" />
            <div>
              <span className="swd-mini-label">Total Commission</span>
              <span className="swd-mini-value">₹{formatAmount(stats.totalCommission)}</span>
            </div>
          </div>

          {/* ✅ NEW - Visit Commission Card */}
          <div className="swd-mini-card">
            <EyeOutlined className="swd-mini-icon" style={{ color: '#8b5cf6' }} />
            <div>
              <span className="swd-mini-label">Visit Commission</span>
              <span className="swd-mini-value">₹{formatAmount(stats.visitCommission || 0)}</span>
            </div>
          </div>

          <div className="swd-mini-card">
            <ClockCircleOutlined className="swd-mini-icon pending" />
            <div>
              <span className="swd-mini-label">Pending ({stats.pendingCount})</span>
              <span className="swd-mini-value">₹{formatAmount(stats.pendingAmount)}</span>
            </div>
          </div>

          <div className="swd-mini-card">
            <CheckCircleOutlined className="swd-mini-icon completed" />
            <div>
              <span className="swd-mini-label">Completed</span>
              <span className="swd-mini-value">{stats.completedCount}</span>
            </div>
          </div>

          <div className="swd-mini-card">
            <CloseCircleOutlined className="swd-mini-icon rejected" />
            <div>
              <span className="swd-mini-label">Rejected</span>
              <span className="swd-mini-value">{stats.rejectedCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MONTHLY GRAPH ===== */}
      <div className="swd-chart-card">
        <div className="swd-chart-header">
          <div className="swd-chart-title-block">
            <div className="swd-chart-icon"><RiseOutlined /></div>
            <div>
              <h3>Monthly Withdrawal Analysis</h3>
              <p>Commission withdrawal history by month</p>
            </div>
          </div>
          <div className="swd-chart-tag">
            Total: ₹{formatAmount(monthlyData.reduce((s, m) => s + m.amount, 0))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="swdGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff0055" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#ff0055" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#888", fontSize: 11 }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: "#888", fontSize: 11 }}
              tickFormatter={(v) => v >= 1000 ? `₹${v / 1000}K` : `₹${v}`} />
            <ChartTooltip
              formatter={(v) => [`₹${formatAmount(v)}`, "Amount"]}
              contentStyle={{ borderRadius: 8, border: "1px solid #f0f0f0", fontSize: 12 }}
            />
            <Area type="monotone" dataKey="amount" stroke="#ff0055" strokeWidth={2.5}
              fill="url(#swdGradient)" dot={{ fill: "#ff0055", r: 3 }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

            {/* ===== WITHDRAWAL HISTORY (Desktop Only) ===== */}
      <div className="swd-table-card swd-desktop-only">
        <div className="swd-table-header">
          <h3><HistoryOutlined /> Withdrawal History</h3>
          <p>{withdrawals.length} records</p>
        </div>
        <Table
          rowKey={(record, index) => `wd-${record.id}-${index}`}
          columns={columns}
          dataSource={withdrawals}
          loading={loading}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            size: "small",
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          scroll={{ x: 800 }}
          className="swd-table"
        />
      </div>

      {/* ===== MOBILE CARDS (Mobile Only) ===== */}
      <div className="swd-mobile-cards swd-mobile-only">
        <div className="swd-table-header" style={{ padding: '0 8px 12px', borderBottom: 'none', marginBottom: 0 }}>
          <h3 style={{ fontSize: 16, margin: 0, color: '#1a1a2e' }}><HistoryOutlined /> Withdrawal History</h3>
          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>{withdrawals.length} records</p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 12, color: '#999' }}>Loading history...</p>
          </div>
        ) : withdrawals.length > 0 ? (
          withdrawals.map((wd) => {
            const cfg = getStatusConfig(wd.status);
            const refNo = generateRefNumber("withdrawal", wd.id);
            const isVisit = wd.plan_name === 'Profile Visit Commission' ||
              (wd.ref_id && String(wd.ref_id).startsWith('VISIT-'));

            return (
              <div key={`mcard-${wd.id}`} className="swd-mcard">
                {/* Top: Ref + Status */}
                <div className="swd-mcard-top">
                  <span className="swd-mcard-ref">{refNo}</span>
                  <span className={`swd-mcard-status swd-status-${cfg.color}`}>
                    {cfg.icon} {cfg.label}
                  </span>
                </div>

                {/* Body: Amount + Info */}
                <div className="swd-mcard-body">
                  <div className="swd-mcard-amount-box">
                    <span className="swd-mcard-amount-label">Withdrawal Amount</span>
                    <span className="swd-mcard-amount-value">₹{formatAmount(wd.amount)}</span>
                  </div>

                  <div className="swd-mcard-info-grid">
                    <div className="swd-mcard-info-item">
                      <span className="label">Commission Type</span>
                      <span className="value">
                        {isVisit ? (
                          <Tag style={{ background: '#f5f3ff', color: '#8b5cf6', border: 'none', margin: 0, fontSize: 10, fontWeight: 700 }}>
                            👀 Visit Comm.
                          </Tag>
                        ) : (
                          <Tag style={{ background: '#ecfdf5', color: '#10b981', border: 'none', margin: 0, fontSize: 10, fontWeight: 700 }}>
                            💰 Plan Comm.
                          </Tag>
                        )}
                      </span>
                    </div>
                    <div className="swd-mcard-info-item">
                      <span className="label">Date</span>
                      <span className="value date">
                        <CalendarOutlined /> {formatDate(wd.created_at || wd.created_date || wd.date)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer: View Button */}
                <div className="swd-mcard-footer">
                  <button 
                    className="swd-mcard-btn-view"
                    onClick={() => { setSelectedWd(wd); setDrawerOpen(true); }}
                  >
                    <EyeOutlined /> View Full Details
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="swd-empty">
            <WalletOutlined className="swd-empty-icon" />
            <h3>No Withdrawal Records</h3>
            <p>Your withdrawal history will appear here</p>
          </div>
        )}
      </div>

      {/* ===== EMPTY STATE ===== */}
      {withdrawals.length === 0 && !loading && (
        <div className="swd-empty">
          <WalletOutlined className="swd-empty-icon" />
          <h3>No Withdrawal Records</h3>
          <p>Your withdrawal history will appear here</p>
        </div>
      )}

      {/* ============================================================
          ✅ WITHDRAWAL MODAL
      ============================================================ */}
      <Modal
        title={
          <div className="swd-modal-title">
            <WalletOutlined /> Request Withdrawal
          </div>
        }
        open={withdrawModalOpen}
        onCancel={() => setWithdrawModalOpen(false)}
        footer={null}
        width={480}
        className="swd-withdraw-modal"
      >
        <div className="swd-modal-balance">
          <div className="swd-modal-balance-label">Available Balance</div>
          <div className="swd-modal-balance-value">
            ₹{formatAmount(stats.availableBalance)}
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleWithdrawSubmit}
          initialValues={{ amount: null }}
        >
          <Form.Item
            label="Withdrawal Amount (₹)"
            name="amount"
            rules={[
              { required: true, message: "Please enter amount" },
              { type: "number", min: 0.01, message: "Amount must be at least ₹0.01" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  if (value <= stats.availableBalance) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(`Maximum amount: ₹${formatAmount(stats.availableBalance)}`));
                }
              })
            ]}
          >
            <InputNumber
              className="swd-amount-input"
              placeholder="Enter amount (e.g., 150.50)"
              size="large"
              style={{ width: "100%" }}
              min={0.01}
              max={stats.availableBalance}
              step={0.01}
              precision={2}
              formatter={(value) => {
                if (!value) return '';
                const parts = value.toString().split('.');
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                return `₹ ${parts.join('.')}`;
              }}
              parser={(value) => value?.replace(/₹\s?|(,*)/g, "")}
              onChange={(value) => setWithdrawAmount(value)}
            />
          </Form.Item>

          {withdrawAmount && withdrawAmount > 0 && (
            <div className="swd-modal-summary">
              <div className="swd-modal-summary-row">
                <span>Withdrawal Amount</span>
                <span className="swd-modal-summary-value">
                  ₹{formatAmount(withdrawAmount)}
                </span>
              </div>
              <div className="swd-modal-summary-row">
                <span>Processing Fee</span>
                <span className="swd-modal-summary-free">₹0.00</span>
              </div>
              <div className="swd-modal-summary-row total">
                <span>Total Deduction</span>
                <span className="swd-modal-summary-value">
                  ₹{formatAmount(withdrawAmount)}
                </span>
              </div>
              <div className="swd-modal-summary-row">
                <span>Remaining Balance</span>
                <span className="swd-modal-summary-remaining">
                  ₹{formatAmount(stats.availableBalance - withdrawAmount)}
                </span>
              </div>
            </div>
          )}

          <div className="swd-modal-actions">
            <Button onClick={() => setWithdrawModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={withdrawing}
              disabled={!withdrawAmount || withdrawAmount <= 0}
              className="swd-modal-submit-btn"
            >
              Submit Withdrawal Request
            </Button>
          </div>

          <div className="swd-modal-note">
            <ExclamationCircleOutlined />
            Withdrawal requests are processed within 24-48 hours.
            You'll be notified when the status changes.
          </div>
        </Form>
      </Modal>

      {/* ============================================================
          ✅ DETAILS DRAWER
      ============================================================ */}
      <Drawer
        title={null}
        placement="right"
        width={500}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        className="swd-drawer-pro"
        headerStyle={{ display: 'none' }}
        bodyStyle={{ padding: 0 }}
      >
        {selectedWd && (() => {
          const cfg = getStatusConfig(selectedWd.status);
          const refNo = generateRefNumber("withdrawal", selectedWd.id);
          const isVisit = selectedWd.plan_name === 'Profile Visit Commission' ||
            (selectedWd.ref_id && String(selectedWd.ref_id).startsWith('VISIT-'));

          return (
            <div className="swdd-container">
              {/* ✅ Hero Section */}
              <div className="swdd-hero">
                <button className="swdd-close-btn" onClick={() => setDrawerOpen(false)}>✕</button>

                <div className="swdd-hero-bg"></div>

                <div className="swdd-hero-content">
                  {/* Icon */}
                  <div className="swdd-hero-icon">
                    <WalletOutlined />
                  </div>

                  {/* Amount */}
                  <div className="swdd-amount-label">Withdrawal Amount</div>
                  <div className="swdd-amount-value">
                    ₹{formatAmount(selectedWd.amount)}
                  </div>

                  {/* Ref No */}
                  <div className="swdd-ref-badge">
                    <IdcardOutlined /> {refNo}
                  </div>

                  {/* Status */}
                  <div className={`swdd-status-badge swdd-status-${cfg.color}`}>
                    {cfg.icon}
                    <span>{cfg.label}</span>
                  </div>

                  {/* Progress */}
                  <div className="swdd-progress-wrap">
                    <Progress
                      percent={cfg.progress}
                      size="small"
                      showInfo={false}
                      strokeColor={
                        selectedWd.status === "rejected"
                          ? "#dc2626"
                          : selectedWd.status === "completed"
                            ? "#22c55e"
                            : "#fff"
                      }
                      trailColor="rgba(255,255,255,0.25)"
                    />
                    <div className="swdd-progress-text">
                      {cfg.description}
                    </div>
                  </div>
                </div>
              </div>

              {/* ✅ Body */}
              <div className="swdd-body">

                {/* Withdrawal Info */}
                <div className="swdd-section">
                  <div className="swdd-section-header">
                    <span className="swdd-section-icon">📋</span>
                    <span className="swdd-section-title">Withdrawal Information</span>
                  </div>

                  <div className="swdd-grid">
                    <div className="swdd-item">
                      <div className="swdd-item-icon"><IdcardOutlined /></div>
                      <div className="swdd-item-content">
                        <span className="swdd-item-label">Reference No</span>
                        <span className="swdd-item-value swdd-highlight">{refNo}</span>
                      </div>
                    </div>

                    <div className="swdd-item">
                      <div className="swdd-item-icon"><DollarOutlined /></div>
                      <div className="swdd-item-content">
                        <span className="swdd-item-label">Amount</span>
                        <span className="swdd-item-value swdd-amount-text">
                          ₹{formatAmount(selectedWd.amount)}
                        </span>
                      </div>
                    </div>

                    <div className="swdd-item">
                      <div className="swdd-item-icon">
                        {isVisit ? '👀' : '💰'}
                      </div>
                      <div className="swdd-item-content">
                        <span className="swdd-item-label">Commission Type</span>
                        <span className="swdd-item-value">
                          {isVisit ? 'Visit Commission' : 'Plan Commission'}
                        </span>
                      </div>
                    </div>

                    <div className="swdd-item">
                      <div className="swdd-item-icon"><WalletOutlined /></div>
                      <div className="swdd-item-content">
                        <span className="swdd-item-label">Type</span>
                        <span className="swdd-item-value">
                          {capitalize(selectedWd.type) || "Withdrawal"}
                        </span>
                      </div>
                    </div>

                    <div className="swdd-item">
                      <div className="swdd-item-icon"><CheckCircleOutlined /></div>
                      <div className="swdd-item-content">
                        <span className="swdd-item-label">Status</span>
                        <span className={`swdd-item-value swdd-status-text-${cfg.color}`}>
                          {cfg.label}
                        </span>
                      </div>
                    </div>

                    <div className="swdd-item">
                      <div className="swdd-item-icon"><CalendarOutlined /></div>
                      <div className="swdd-item-content">
                        <span className="swdd-item-label">Request Date</span>
                        <span className="swdd-item-value">
                          {formatDateTime(selectedWd.created_at || selectedWd.created_date || selectedWd.date)}
                        </span>
                      </div>
                    </div>

                    {selectedWd.processed_at && (
                      <div className="swdd-item">
                        <div className="swdd-item-icon"><CheckCircleOutlined /></div>
                        <div className="swdd-item-content">
                          <span className="swdd-item-label">Processed On</span>
                          <span className="swdd-item-value">
                            {formatDateTime(selectedWd.processed_at)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bank Details */}
                <div className="swdd-section">
                  <div className="swdd-section-header">
                    <span className="swdd-section-icon">🏦</span>
                    <span className="swdd-section-title">Bank Details</span>
                  </div>

                  <div className="swdd-grid">
                    <div className="swdd-item">
                      <div className="swdd-item-icon"><BankOutlined /></div>
                      <div className="swdd-item-content">
                        <span className="swdd-item-label">Account Number</span>
                        <span className="swdd-item-value">
                          {selectedWd.bank_account || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="swdd-item">
                      <div className="swdd-item-icon"><IdcardOutlined /></div>
                      <div className="swdd-item-content">
                        <span className="swdd-item-label">IFSC Code</span>
                        <span className="swdd-item-value">
                          {selectedWd.ifsc_code || "N/A"}
                        </span>
                      </div>
                    </div>

                    {selectedWd.upi_id && (
                      <div className="swdd-item">
                        <div className="swdd-item-icon"><CreditCardOutlined /></div>
                        <div className="swdd-item-content">
                          <span className="swdd-item-label">UPI ID</span>
                          <span className="swdd-item-value">{selectedWd.upi_id}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Note */}
                {selectedWd.admin_note && (
                  <div className="swdd-section">
                    <div className="swdd-section-header">
                      <span className="swdd-section-icon">📝</span>
                      <span className="swdd-section-title">Admin Note</span>
                    </div>
                    <div className="swdd-note-box">
                      {selectedWd.admin_note}
                    </div>
                  </div>
                )}

                {/* Rejection Reason */}
                {selectedWd.status === "rejected" && (
                  <div className="swdd-section">
                    <div className="swdd-section-header">
                      <span className="swdd-section-icon">⚠️</span>
                      <span className="swdd-section-title">Rejection Reason</span>
                    </div>
                    <div className="swdd-rejection-box">
                      <CloseCircleOutlined className="swdd-rejection-icon" />
                      <span>
                        {selectedWd.admin_note || selectedWd.remarks || "No reason provided by admin"}
                      </span>
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="swdd-section">
                  <div className="swdd-section-header">
                    <span className="swdd-section-icon">⏱️</span>
                    <span className="swdd-section-title">Status Timeline</span>
                  </div>

                  <div className="swdd-timeline">
                    {/* Step 1: Submitted */}
                    <div className="swdd-timeline-item">
                      <div className="swdd-timeline-dot done">
                        <CheckCircleOutlined />
                      </div>
                      <div className="swdd-timeline-content">
                        <div className="swdd-timeline-title">Request Submitted</div>
                        <div className="swdd-timeline-date">
                          {formatDateTime(selectedWd.created_at || selectedWd.created_date)}
                        </div>
                      </div>
                    </div>

                    {/* Step 2: Approved (if not pending) */}
                    {selectedWd.status !== "pending" && selectedWd.status !== "rejected" && (
                      <div className="swdd-timeline-item">
                        <div className="swdd-timeline-dot done">
                          <CheckCircleOutlined />
                        </div>
                        <div className="swdd-timeline-content">
                          <div className="swdd-timeline-title">Request Approved</div>
                          <div className="swdd-timeline-date">
                            {formatDateTime(selectedWd.processed_at || selectedWd.updated_at)}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Completed */}
                    {selectedWd.status === "completed" && (
                      <div className="swdd-timeline-item">
                        <div className="swdd-timeline-dot done success">
                          <CheckCircleOutlined />
                        </div>
                        <div className="swdd-timeline-content">
                          <div className="swdd-timeline-title">Amount Transferred</div>
                          <div className="swdd-timeline-date">
                            {formatDateTime(selectedWd.processed_at || selectedWd.updated_at)}
                          </div>
                          {selectedWd.admin_note && (
                            <div className="swdd-timeline-note">{selectedWd.admin_note}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Rejected State */}
                    {selectedWd.status === "rejected" && (
                      <div className="swdd-timeline-item">
                        <div className="swdd-timeline-dot rejected">
                          <CloseCircleOutlined />
                        </div>
                        <div className="swdd-timeline-content">
                          <div className="swdd-timeline-title">Request Rejected</div>
                          <div className="swdd-timeline-date">
                            {formatDateTime(selectedWd.processed_at || selectedWd.updated_at)}
                          </div>
                          {selectedWd.admin_note && (
                            <div className="swdd-timeline-note">{selectedWd.admin_note}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Pending State */}
                    {selectedWd.status === "pending" && (
                      <div className="swdd-timeline-item">
                        <div className="swdd-timeline-dot pending">
                          <ClockCircleOutlined />
                        </div>
                        <div className="swdd-timeline-content">
                          <div className="swdd-timeline-title">Awaiting Admin Approval</div>
                          <div className="swdd-timeline-date">In Progress...</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
}

export default StaffWithdrawals;